import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FilterModel } from '../../../common/model/ui/filter.model';
import { isFilterApplied } from '../../../common/utils/services/list-filter-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvListFilterControl from '../molecules/OvListFilterControl';

interface OvListFilterBarProps {
  filters: FilterModel[];
  onFilterChange: (filter: FilterModel) => void;
  onClearFilters: () => void;
  children?: ReactNode;
  renderEditorFooter?: (filter: FilterModel, close: () => void) => ReactNode;
}

const OvListFilterBar: FC<OvListFilterBarProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  children = null,
  renderEditorFooter,
}) => {
  const { t } = useTranslation();
  const [openFieldName, setOpenFieldName] = useState<string | null>(null);
  const appliedCount = filters.filter(isFilterApplied).length;

  const close = (fieldName: string) =>
    setOpenFieldName((current) => (current === fieldName ? null : current));

  return (
    <>
      <Strip>
        <Controls role="group" aria-label={t('common.filters')}>
          {filters.map((filter) => (
            <OvListFilterControl
              key={filter.fieldName}
              filter={filter}
              isOpen={openFieldName === filter.fieldName}
              onToggle={() =>
                setOpenFieldName((current) =>
                  current === filter.fieldName ? null : filter.fieldName
                )
              }
              onClose={() => close(filter.fieldName)}
              footer={renderEditorFooter?.(filter, () =>
                close(filter.fieldName)
              )}
              onApply={(value) => {
                close(filter.fieldName);
                onFilterChange({ ...filter, value });
              }}
            />
          ))}
        </Controls>
        {children}
      </Strip>
      <Summary aria-live="polite">
        {appliedCount > 0 && (
          <>
            <span>
              {t('common.listFilters.appliedCount', { count: appliedCount })}
            </span>
            <ClearAll
              type="button"
              onClick={() => {
                setOpenFieldName(null);
                onClearFilters();
              }}
            >
              {t('common.listFilters.clearAll')}
            </ClearAll>
          </>
        )}
      </Summary>
    </>
  );
};

export default OvListFilterBar;

const Strip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.875rem 0;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Controls = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;

  @media (max-width: ${breakpoints.lg}) {
    flex-basis: 100%;
  }

  @media (max-width: ${breakpoints.sm}) {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;

    > * {
      flex: none;
    }
  }
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-top: 0.625rem;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};

  &:empty {
    display: none;
  }
`;

const ClearAll = styled.button`
  min-height: 1.75rem;
  padding: 0.1875rem 0.3125rem;
  border: 0;
  border-radius: ${Variables.borderRadius.SMALL};
  background: transparent;
  color: ${Colours.OV_BASE};
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;
